import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "../ui/dialog";
import { Workout } from "./workoutsColumns";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc } from "@/lib/trpc/client";
import clsx from "clsx";
import { ScrollArea } from "../ui/scroll-area";
import {
  apiWorkoutToEditor,
  cloneEditorWorkout,
} from "@/lib/utils/workoutSections";
import {
  Search,
  Dumbbell,
  Clock,
  Link2,
  Target,
  ArrowLeft,
  Layers,
} from "lucide-react";
import {
  DistanceUnit,
  formatDistance,
  formatTimeSeconds,
} from "@/lib/utils/exerciseTracking";
import {
  WorkoutImportPreview,
  workoutImportSummary,
} from "@/components/workouts/WorkoutImportPreview";

interface ImportWorkoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWorkoutSelected: (workout: Workout) => void;
}

const ImportWorkoutDialog: React.FC<ImportWorkoutDialogProps> = ({
  open,
  onOpenChange,
  onWorkoutSelected,
}) => {
  const org = useOrg();
  const distanceUnitLabel: DistanceUnit =
    org?.distanceUnitDefault === "mi" ? "mi" : "km";
  const [searchTerm, setSearchTerm] = useState("");
  const [showWorkoutDetails, setShowWorkoutDetails] = useState(false);

  const getSetTargetLabel = (
    trackingType: string | null,
    set: {
      startRange?: number;
      endRange?: number;
      targetDistanceMeters?: number;
      targetTimeSeconds?: number;
    },
  ) => {
    if (trackingType?.startsWith("distance")) {
      const distanceLabel = formatDistance(
        set.targetDistanceMeters,
        distanceUnitLabel,
        trackingType,
      );
      const timeLabel = set.targetTimeSeconds
        ? ` in ${formatTimeSeconds(set.targetTimeSeconds)}`
        : "";
      return `${distanceLabel}${timeLabel}`.trim();
    }
    if (trackingType === "timed") {
      return formatTimeSeconds(set.targetTimeSeconds);
    }
    return `${set.startRange} - ${set.endRange} reps`;
  };

  const { data } = trpc.workouts.list.useQuery(undefined, {
    enabled: Boolean(open && org?.id),
    refetchOnMount: "always",
  });
  type TemplateWorkout = NonNullable<typeof data>[number];
  const [selectedWorkout, setWorkout] = useState<TemplateWorkout | undefined>();

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setShowWorkoutDetails(false);
      return;
    }
    if (!data?.length) {
      setWorkout(undefined);
      return;
    }
    setWorkout((current) => {
      const match = current
        ? data.find((row) => row.id === current.id)
        : undefined;
      return match ?? data[0];
    });
  }, [data, open]);

  const filteredWorkouts =
    data?.filter(
      (workout) =>
        workout.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        workout.description?.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  const selectedEditor = useMemo(
    () => (selectedWorkout ? apiWorkoutToEditor(selectedWorkout) : null),
    [selectedWorkout],
  );
  const selectedSummary = selectedEditor
    ? workoutImportSummary(selectedEditor)
    : null;

  const handleWorkoutSelect = (workout: TemplateWorkout) => {
    setWorkout(workout);
    if (window.innerWidth < 768) {
      setShowWorkoutDetails(true);
    }
  };

  const handleBackToList = () => {
    setShowWorkoutDetails(false);
  };

  const summaryFor = (workout: TemplateWorkout) =>
    workoutImportSummary(apiWorkoutToEditor(workout));

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      analyticsName="import_workout_dialog"
    >
      <DialogContent className="flex h-[90vh] w-[95vw] max-w-6xl flex-col gap-0 p-0">
        <DialogHeader className="border-b px-6 py-4">
          <h2 className="text-2xl font-bold text-foreground">
            Import Workout Template
          </h2>
          <p className="text-muted-foreground">
            Select a workout template to import into this plan
          </p>
        </DialogHeader>

        <div className="flex h-full flex-col overflow-hidden md:flex-row">
          <div
            className={clsx(
              "border-r bg-muted/30",
              showWorkoutDetails ? "hidden md:block md:w-80" : "w-full md:w-80",
            )}
          >
            <div className="border-b p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search workouts..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full rounded-lg border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <ScrollArea className="h-[calc(90vh-200px)] md:h-[calc(90vh-240px)]">
              <div className="space-y-2 p-2">
                {filteredWorkouts.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    <Dumbbell className="mx-auto mb-2 h-12 w-12 opacity-50" />
                    <p>No workouts found</p>
                  </div>
                ) : (
                  filteredWorkouts.map((workout) => {
                    const summary = summaryFor(workout);
                    return (
                      <Card
                        key={workout.id}
                        onClick={() => handleWorkoutSelect(workout)}
                        className={clsx(
                          "cursor-pointer select-none border-2 p-4 transition-all duration-200 hover:shadow-md",
                          workout.id === selectedWorkout?.id
                            ? "border-primary bg-primary/5 shadow-md"
                            : "border-border hover:border-primary/30",
                        )}
                      >
                        <div className="space-y-2">
                          <h3 className="line-clamp-1 font-semibold text-foreground">
                            {workout.name}
                          </h3>
                          {workout.description ? (
                            <p className="line-clamp-2 text-sm text-muted-foreground">
                              {workout.description}
                            </p>
                          ) : null}
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                              <Target className="h-3 w-3" />
                              {summary.exerciseCount}{" "}
                              {summary.exerciseCount === 1
                                ? "exercise"
                                : "exercises"}
                            </span>
                            {summary.sectionCount > 0 ? (
                              <span className="inline-flex items-center gap-1">
                                <Layers className="h-3 w-3" />
                                {summary.sectionCount}{" "}
                                {summary.sectionCount === 1
                                  ? "section"
                                  : "sections"}
                              </span>
                            ) : null}
                            {summary.supersetCount > 0 ? (
                              <span className="inline-flex items-center gap-1">
                                <Link2 className="h-3 w-3" />
                                {summary.supersetCount}{" "}
                                {summary.supersetCount === 1
                                  ? "superset"
                                  : "supersets"}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </Card>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>

          <div className="hidden min-h-0 flex-1 flex-col overflow-hidden md:flex">
            {selectedWorkout && selectedEditor ? (
              <>
                <div className="flex-shrink-0 border-b bg-gradient-to-r from-background to-muted/20 p-6">
                  <div className="space-y-3">
                    <h1 className="text-2xl font-bold text-foreground">
                      {selectedWorkout.name}
                    </h1>
                    {selectedWorkout.description ? (
                      <p className="line-clamp-3 text-lg leading-relaxed text-muted-foreground">
                        {selectedWorkout.description}
                      </p>
                    ) : null}
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        <span>
                          {selectedSummary?.exerciseCount ?? 0} exercises
                        </span>
                      </div>
                      {selectedSummary && selectedSummary.sectionCount > 0 ? (
                        <div className="flex items-center gap-2">
                          <Layers className="h-4 w-4" />
                          <span>
                            {selectedSummary.sectionCount} sections
                          </span>
                        </div>
                      ) : null}
                      {selectedSummary && selectedSummary.supersetCount > 0 ? (
                        <div className="flex items-center gap-2">
                          <Link2 className="h-4 w-4" />
                          <span>
                            {selectedSummary.supersetCount} supersets
                          </span>
                        </div>
                      ) : null}
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>Template</span>
                      </div>
                    </div>
                  </div>
                </div>

                <ScrollArea className="min-h-0 flex-1">
                  <div className="p-6">
                    <WorkoutImportPreview
                      workout={selectedEditor}
                      getSetTargetLabel={getSetTargetLabel}
                    />
                  </div>
                </ScrollArea>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <Dumbbell className="mx-auto mb-4 h-16 w-16 opacity-50" />
                  <p className="text-lg">Select a workout to view details</p>
                </div>
              </div>
            )}
          </div>

          <div
            className={clsx(
              "flex min-h-0 flex-1 flex-col overflow-hidden md:hidden",
              showWorkoutDetails ? "block" : "hidden",
            )}
          >
            {selectedWorkout && selectedEditor ? (
              <>
                <div className="flex-shrink-0 border-b bg-gradient-to-r from-background to-muted/20 p-4">
                  <div className="mb-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleBackToList}
                      className="rounded-lg p-2 transition-colors hover:bg-muted/50"
                    >
                      <ArrowLeft className="h-5 w-5 text-foreground" />
                    </button>
                    <h1 className="text-xl font-bold text-foreground">
                      {selectedWorkout.name}
                    </h1>
                  </div>

                  {selectedWorkout.description ? (
                    <p className="ml-12 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {selectedWorkout.description}
                    </p>
                  ) : null}
                </div>

                <ScrollArea className="min-h-0 flex-1">
                  <div className="p-4">
                    <WorkoutImportPreview
                      workout={selectedEditor}
                      compact
                      getSetTargetLabel={getSetTargetLabel}
                    />
                  </div>
                </ScrollArea>
              </>
            ) : null}
          </div>
        </div>

        <DialogFooter className="border-t bg-muted/20 px-6 py-4">
          <div className="flex w-full gap-3 sm:w-auto">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              disabled={!selectedEditor}
              onClick={() => {
                if (selectedEditor) {
                  onWorkoutSelected(cloneEditorWorkout(selectedEditor));
                  onOpenChange(false);
                }
              }}
              className="flex-1 sm:flex-none"
            >
              Import Workout
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportWorkoutDialog;
